import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useAssetTypes } from '../../api/queries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import type { ImportEntityCount } from './importTypes'
import { EmptyState } from '../ui/empty-state'
import { LotBreakdown } from '../assets/LotBreakdown'
import { isLotTypeCode } from '../../lib/asset-type-tree'
import { lotBreakdown } from '../../lib/lot-breakdown'

interface ImportEntityCardsProps {
    org: string
    importUid: string
    entities: ImportEntityCount[]
}

/**
 * "Ce qu'on en a construit" (spec §7.3): one card per entity kind (every lot
 * type folded into one "Lots" card, broken down by nature), each
 * carrying the "issus de N documents" attribution, and each number navigating
 * to the matching list filtered by `?import_uid=`.
 */
export function ImportEntityCards({ org, importUid, entities }: ImportEntityCardsProps) {
    const { t } = useTranslation('imports')
    const { locale } = useLocale()

    // Asset type labels come from the ontology (asset_types.name jsonb), not
    // from an i18n file — a custom type an org adds is localized too.
    const { data: assetTypes = [] } = useAssetTypes(org)
    const assetTypeNames = useMemo(() => {
        const byCode: Record<string, string> = {}
        for (const at of assetTypes as Array<{ code?: string; name?: Record<string, string> }>) {
            if (at.code) byCode[at.code] = getLocalizedName(at.name, locale) || at.code
        }
        return byCode
    }, [assetTypes, locale])

    const lotCounts: Record<string, number> = {}
    for (const e of entities) {
        if (e.type === 'asset' && e.asset_type && isLotTypeCode(e.asset_type)) {
            lotCounts[e.asset_type] = (lotCounts[e.asset_type] ?? 0) + e.count
        }
    }
    const lotsTotal = lotBreakdown(lotCounts).total
    const firstLotIndex = entities.findIndex(e => e.type === 'asset' && isLotTypeCode(e.asset_type))

    if (entities.length === 0) {
        return (
            <section data-testid="import-entities" className="space-y-3">
                <h2 className="text-sm font-medium text-muted-foreground">{t('entities.title')}</h2>
                <EmptyState title={t('entities.empty')} />
            </section>
        )
    }

    return (
        <section data-testid="import-entities" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('entities.title')}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {entities.map((entity, index) => {
                    // Every lot type folds into one "Lots" card, placed where
                    // the first lot entry sat (spec 2026-10-11-19). It is a div:
                    // its lines are the links. No "from N documents" line, the
                    // per-type document counts cannot be summed.
                    if (entity.type === 'asset' && isLotTypeCode(entity.asset_type)) {
                        if (index !== firstLotIndex) return null
                        return (
                            <div
                                key="lots"
                                data-testid="import-entity-lots"
                                className="rounded-lg border border-border bg-card p-4"
                            >
                                <p className="text-sm font-medium text-foreground">{t('entities.lots')}</p>
                                <p
                                    className="mt-1 text-2xl font-bold text-foreground"
                                    data-testid="import-entity-lots-total"
                                >
                                    {lotsTotal}
                                </p>
                                <LotBreakdown
                                    org={org}
                                    counts={lotCounts}
                                    scope={{ import_uid: importUid }}
                                    testId="import-entity-lots"
                                    className="mt-1"
                                />
                            </div>
                        )
                    }
                    const key = entity.asset_type
                        ? `asset:${entity.asset_type}`
                        : `type:${entity.type}`
                    const title = entity.asset_type
                        ? assetTypeNames[entity.asset_type] || entity.asset_type
                        : t(`entities.type.${entity.type}`, { defaultValue: entity.type })

                    const card = (
                        <>
                            <p className="text-sm font-medium text-foreground">{title}</p>
                            <p
                                className="mt-1 text-2xl font-bold text-foreground"
                                data-testid={`import-entity-${key}-count`}
                            >
                                {entity.count}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {entity.from_documents > 0
                                    ? t('entities.from_documents', { count: entity.from_documents })
                                    : t('entities.from_no_document')}
                            </p>
                        </>
                    )

                    // Only assets have a list view that understands
                    // `?import_uid=` today; the others render as plain cards
                    // rather than dead links.
                    if (entity.type === 'asset') {
                        return (
                            <Link
                                key={key}
                                to="/$org/assets"
                                params={{ org }}
                                search={{
                                    q: undefined,
                                    flag: undefined,
                                    asset_type_code: entity.asset_type || undefined,
                                    asset_type_uid: undefined,
                                    showBuildingGroups: undefined,
                                    showBuildings: undefined,
                                    archived: undefined,
                                    import_uid: importUid,
                                }}
                                data-testid={`import-entity-${key}`}
                                className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40"
                            >
                                {card}
                            </Link>
                        )
                    }

                    return (
                        <div
                            key={key}
                            data-testid={`import-entity-${key}`}
                            className="rounded-lg border border-border bg-card p-4"
                        >
                            {card}
                        </div>
                    )
                })}
            </div>
        </section>
    )
}
